import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "summary", "badge"]
  static values = {
    clearLabel: String
  }

  connect() {
    this.handleSummaryClick = this.handleSummaryClick.bind(this)
    this.element.addEventListener("click", this.handleSummaryClick)

    this.renderSummary()
  }

  disconnect() {
    this.element.removeEventListener("click", this.handleSummaryClick)
  }

  inputChanged() {
    this.renderSummary()
  }

  handleSummaryClick(e) {
    const removeBtn = e.target.closest("[data-filter-remove-key]")
    if (removeBtn) {
      e.preventDefault()
      this.removeFilter(removeBtn.dataset.filterRemoveKey)
      return
    }

    if (e.target.closest("[data-filter-clear-all]")) {
      e.preventDefault()
      this.clearAllFilters()
    }
  }

  removeFilter(key) {
    const input = this.findInput(key)
    if (!input) return

    this.clearInput(input)

    this.renderSummary()

    // ensure DOM state is applied before submit
    requestAnimationFrame(() => {
      input.form?.requestSubmit?.()
    })
  }

  clearAllFilters() {
    this.inputTargets.forEach(input => {
      if (input.dataset.default === "true") return
      this.clearInput(input)
    })

    this.renderSummary()

    requestAnimationFrame(() => {
      this.inputTargets[0]?.form?.requestSubmit?.()
    })
  }

  clearInput(input) {
    const isRadio = input.type === "radio"
    const isCheckbox = input.type === "checkbox"

    if (isRadio) {
      this.clearRadioGroup(input.name || input.dataset.filterKey)
      return
    }

    if (isCheckbox) {
      input.checked = false
      return
    }

    input.value = ""
  }

  clearRadioGroup(name) {
    this.inputTargets
      .filter(el => (el.name || el.dataset.filterKey) === name)
      .forEach(radio => {
        radio.checked = false
      })
  }

  findInput(key) {
    return this.inputTargets.find(el => {
      return (el.name || el.dataset.filterKey) === key
    })
  }

  renderSummary() {
    const filters = this.collectFilters()

    this.summaryTarget.innerHTML = filters.length
      ? this.renderFilters(filters) + this.renderClearAll()
      : ""
    
    this.updateBadge(filters.length)
  }

  updateBadge(count) {
    if (this.hasBadgeTarget) {
      if (count > 0) {
        this.badgeTarget.textContent = count
        this.badgeTarget.style.display = "inline-block"
      } else {
        this.badgeTarget.style.display = "none"
      }
    }
  }

  collectFilters() {
    return this.inputTargets.reduce((acc, input) => {
      const key = input.name || input.dataset.filterKey
      if (!key) return acc

      if (input.dataset.default === "true") return acc

      const isRadio = input.type === "radio"
      const isCheckbox = input.type === "checkbox"

      if (isRadio || isCheckbox) {
        if (!input.checked) return acc
      } else {
        if (!input.value) return acc
      }

      const value = this.getDisplayValue(input)
      if (!value) return acc

      acc.push({
        key,
        label: `${input.dataset.label}: ${value}`
      })

      return acc
    }, [])
  }

  getDisplayValue(input) {
    if (input.tagName === "SELECT") {
      return input.options[input.selectedIndex]?.text || null
    }

    if (input.type === "checkbox" || input.type === "radio") {
      return input.checked ? (input.dataset.name || input.value) : null
    }

    return input.value || null
  }

  renderFilters(filters) {
    return `
      <div class="d-flex flex-wrap align-items-center gap-2">
        ${filters.map(f => `
          <span class="btn btn-filter-active font-sm fw-bold d-inline-flex align-items-center">
            <button
              type="button"
              class="filter-remove btn btn-sm p-0 me-2"
              data-filter-remove-key="${f.key}">
              <i class="bi bi-plus-lg close-icon"></i>
            </button>

            ${f.label}
          </span>
        `).join("")}
      </div>
    `
  }

  renderClearAll() {
    return `
      <span class="btn btn-filter font-sm fw-bold d-inline-flex align-items-center"
            data-filter-clear-all>
        <button type="button" class="filter-remove btn btn-sm p-0 me-2">
        <i class="bi bi-plus-lg close-icon me-1"></i></button>
        ${this.clearLabelValue}
      </span>
    `
  }
}